import { useMutation, useQuery } from "@apollo/client";
import Box from "@material-ui/core/Box";
import { makeStyles } from "@material-ui/core/styles";
import FileSaver from "file-saver";
import React, { FC, useState } from "react";
import { WrappedComponentProps, injectIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { DASHBOARD_URL } from "../../config";
import { SIGNUP_PROGRESSION } from "../../graphql/account/mutation";
import {
  SignupProgression,
  SignupProgressionVariables,
} from "../../graphql/account/types/SignupProgression";
import { GET_CONTRACT_TEMPLATE } from "../../graphql/contract/query";
import { ContractTemplate } from "../../graphql/contract/types/ContractTemplate";
import { ACCESS_TOKEN_KEY, getAccessToken } from "../../services/LocalStorage";
import { base64toBlob } from "../../utils/fileUtils";
import ContractIllustration from "../Assets/contract.svg";
import { Contract } from "./Contract";
import ContractDialogSuccess from "./Contract/ContractDialogSuccess";
import SignedContractTitle from "./SignedContractTitle";
import { signupProgression } from "../../enums/account";
import { CREATE_CONTRACT } from "../../graphql/onboarding/contract/mutation";
import {
  CreateContract,
  CreateContractVariables,
} from "../../graphql/onboarding/contract/types/CreateContract";
import { SapheerError } from "../../enums/error";
import { CustomButton } from "../Ui/CustomButton";
import { useToastNotifications } from "../../utils/toastUtils";
import { _onboarding } from "../../services/LocalForage";

const styles = makeStyles((theme) => ({
  contract: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    textAlign: "center",
    padding: theme.spacing(3),
    height: "100%",
  },
  circularProgress: {
    margin: "24px auto",
  },
  imageContainer: {
    height: 96,
    display: "flex",
    justifyContent: "center",
  },
  image: {
    maxHeight: "100%",
  },
  button: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: theme.spacing(2, 0),
  },
}));

interface SignedContractProps {
  previous: string;
  title: string;
}

const SignedContract: FC<SignedContractProps & WrappedComponentProps> = (
  props,
) => {
  const classes = styles();
  const { previous, title, intl } = props;
  const [open, setOpen] = useState(false);
  const [dialogSuccessOpen, setDialogSuccessOpen] = useState(false);
  const [contratData64, setContratData64] = useState("");
  const navigate = useNavigate();

  const { showErrorToast, showSuccessToast } = useToastNotifications();
  const [finishStep, { loading: finishStepLoading }] = useMutation<
    SignupProgression,
    SignupProgressionVariables
  >(SIGNUP_PROGRESSION);

  const handleClickOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  // Remplacement de history.push par navigate
  const handlePrevious = () => {
    navigate(previous);
  };

  const {
    data: contractData,
    error: contractTextError,
    loading: contractTextLoading,
  } = useQuery<ContractTemplate>(GET_CONTRACT_TEMPLATE);

  let contractTemplate: string = "";
  if (contractData?.contractTemplate) {
    contractTemplate = contractData.contractTemplate;
  }
  if (contractTextError) {
    showErrorToast({ description: "error.getContract" });
  }

  const [saveContract, { loading: saveSignatureLoading }] = useMutation<
    CreateContract,
    CreateContractVariables
  >(CREATE_CONTRACT, {
    onCompleted: async (data) => {
      if (data?.createContract) {
        setContratData64(data.createContract);
        showSuccessToast({
          description: "success.saveSignature",
        });
        setOpen(false);
        setDialogSuccessOpen(true);
      }
    },
    onError: (error) => {
      const { graphQLErrors } = error;
      let message = "error.saveSignature";
      if (graphQLErrors && graphQLErrors[0] && graphQLErrors[0].message) {
        const { message: _mess } = graphQLErrors[0];
        const TR_TIMEOUT = "ETIMEDOUT";
        if (_mess.includes(TR_TIMEOUT)) {
          message = "error.saveSignature.resend";
        } else if (_mess.includes(SapheerError.STRIPE_PAYMENT_NOT_PAID)) {
          message = "payment.stripe.not.paid";
        }
      }
      showErrorToast({ description: message });
    },
  });

  const sendSignature = (signatureData: string) => {
    if (!!signatureData) {
      saveContract({
        variables: { signature: signatureData },
      });
    }
  };

  const handleDialogSuccessClose = async () => {
    finishStep({
      variables: {
        step: signupProgression.FINISH,
      },
    });
    const token = await getAccessToken();
    localStorage.setItem("token", token);
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    await _onboarding.removeItem(ACCESS_TOKEN_KEY);
    setDialogSuccessOpen(false);
    window.location.href = `${DASHBOARD_URL}/loginFromAccount`;
  };

  const handleDownloadContract = () => {
    const signatureBlob = base64toBlob(contratData64, "application/pdf");
    FileSaver.saveAs(signatureBlob, "Contrat_Sapheer.pdf");
  };

  return (
    <Box className={classes.contract}>
      <Box className={classes.imageContainer}>
        <img
          alt="Illustration"
          src={ContractIllustration}
          className={classes.image}
        />
      </Box>
      {/* Composant titre */}
      <SignedContractTitle existingCompany />
      <Box className={classes.button}>
        <CustomButton
          type="button"
          variant="outline4"
          onClick={handlePrevious}
          label="button.previous"
          disabled={
            saveSignatureLoading || finishStepLoading || contractTextLoading
          }
          fullWidthMobile
        />
        <CustomButton
          type="button"
          variant="primary"
          onClick={handleClickOpen}
          label="contract.button"
          loading={
            saveSignatureLoading || finishStepLoading || contractTextLoading
          }
          fullWidthMobile
        />
      </Box>

      <Box>
        <Contract
          contractTemplate={contractTemplate}
          handleClose={handleClose}
          open={open}
          handleSendSignature={sendSignature}
          title={title}
          loading={
            saveSignatureLoading || finishStepLoading || contractTextLoading
          }
        />
        <ContractDialogSuccess
          handleClose={handleDialogSuccessClose}
          open={dialogSuccessOpen}
          handleDownloadContract={handleDownloadContract}
        />
      </Box>
    </Box>
  );
};

export default injectIntl(SignedContract);
